import { useState } from 'react';
import { Check, Mail, Paperclip, MessageSquare } from 'lucide-react';
import { api, ApiError } from '../lib/api';
import { useI18n } from '../lib/i18n';
import { uploadFile } from '../lib/upload';
import { FileDrop } from './FileDrop';
import { ClaimAppointmentsPanel } from './ClaimAppointmentsPanel';
import { ClaimSchedulingExtras } from './ClaimSchedulingExtras';
import { ClaimChat } from './ClaimChat';
import { ClaimTasksBox } from './ClaimTasksBox';
import { ClaimEmailBox } from './ClaimEmailBox';
import { InfoPendingBox } from './InfoPendingBox';
import { ContractorInvoicesBox } from './ContractorInvoicesBox';

import { ClaimAtrBox } from './ClaimAtrBox';
import { ClaimVideosBox } from './ClaimVideosBox';
import { CaseOverview } from './CaseOverview';
import { ContractorFilesPanel } from './ContractorFilesPanel';
import { ClaimFiles } from './ClaimFiles';
import { ClaimServices } from './ClaimServices';
import { CallRecordings } from './CallRecordings';
import { SupplementEstimateCompare } from './SupplementEstimateCompare';
import { SupplementReview } from './SupplementReview';
import { AutoFollowUpCard } from './AutoFollowUpCard';
import { InspectionContactBox } from './InspectionContactBox';

/**
 * Módulo de subir archivos: se sube cualquier documento, la IA lo LEE y lo
 * CLASIFICA (estimate / invoice / COC / roof report / etc.) y queda guardado en
 * Files del claim. Reemplaza en cada tab de depto a los módulos de estatus/equipo
 * (esos ahora se ven resumidos en la tarjeta de arriba del claim).
 */
function ClaimFileUploadBox({ claimId }: { claimId: string }) {
  const { t } = useI18n();
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [recent, setRecent] = useState<Array<{ name: string; label: string }>>([]);
  async function onUpload(file: File) {
    setBusy(true); setMsg(null);
    try {
      const key = await uploadFile(file, 'misc');
      const r = await api.post<{ id: string; doc_type?: string; doc_label?: string }>(`/api/claims/${claimId}/files`, { fileKey: key, fileName: file.name, mimeType: file.type });
      setRecent((x) => [{ name: file.name, label: r.doc_label || r.doc_type || t('cdt.fileDoc') }, ...x].slice(0, 6));
      setMsg(t('cdt.fileClassified'));
    } catch (e) { setMsg(e instanceof ApiError ? e.message : 'Error'); } finally { setBusy(false); }
  }
  return (
    <div className="card">
      <div className="muted" style={{ fontSize: 'var(--fs-sm)', fontWeight: 'var(--fw-bold)', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 4 }}><Paperclip size={14} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('cdt.uploadFiles')}</div>
      <div className="muted" style={{ fontSize: 'var(--fs-2xs)', marginBottom: 8 }}>{t('cdt.uploadFilesHelp')}</div>
      <div style={{ border: '1.5px dashed var(--line)', borderRadius: 10, padding: 10, background: 'var(--surface-2)' }}>
        <FileDrop onFile={onUpload} accept="application/pdf,.esx,.ESX,image/*" label={t('cdt.uploadDrop')} busy={busy} busyLabel={t('cd.reading')} />
      </div>
      {recent.length > 0 && (
        <div style={{ display: 'grid', gap: 4, marginTop: 8 }}>
          {recent.map((f, i) => (
            <div key={i} style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 'var(--fs-xs)' }}>
              <span className="badge" style={{ fontSize: 'var(--fs-2xs)', background: 'var(--green-soft)', color: 'var(--green)' }}><Check size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {f.label}</span>
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.name}</span>
            </div>
          ))}
        </div>
      )}
      {msg && <div className="muted" style={{ fontSize: 'var(--fs-2xs)', marginTop: 6 }}>{msg}</div>}
    </div>
  );
}

/**
 * Tab de un departamento dentro del claim: updates del depto, comunicación por
 * email, subir archivos (la IA los clasifica), Info Pending y las herramientas
 * propias del depto (Scheduling → citas, Supplements → Negotiation Coach).
 * El estatus del depto + equipo asignado ahora se ven RESUMIDOS en la tarjeta de
 * arriba del claim (ClaimHero), no en cada tab.
 */
export function ClaimDepartmentTab(
  { claimId, deptKey, atrSince, inspectorNotes }: { claimId: string; deptKey: string; atrSince?: string | null; inspectorNotes?: string | null },
) {
  const { t } = useI18n();
  const deptTitle = deptKey.charAt(0).toUpperCase() + deptKey.slice(1);
  // Módulo de email del depto (recogido). Se reubica: en Claims va ARRIBA (debajo del Case
  // Overview, encima de Department Updates); en los demás deptos queda en su lugar de siempre.
  const emailModule = (
    <details className="card">
      <summary style={{ cursor: 'pointer', fontWeight: 600 }}><Mail size={14} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('em.communication')}</summary>
      <div style={{ marginTop: 12 }}><ClaimEmailBox claimId={claimId} departmentKey={deptKey} /></div>
    </details>
  );
  return (
    <div className="grid" style={{ gap: 16 }}>
      {/* Supplements: comparativo primer/último estimado del seguro DE PRIMERO (pedido 2026-07-30),
          igual que en el ClaimDrawer — para ver de un vistazo cuánto subimos con el supplement. */}
      {deptKey === 'supplements' && <SupplementEstimateCompare claimId={claimId} />}
      {/* Supplement review (Reviewing / Sent to carrier / Doesn't apply): el mismo cuadro del drawer, apenas
          debajo de los números — pedido de Manuel 2026-10-02 ("ahí apenas salgan los números, ahí lo quiero"). */}
      {deptKey === 'supplements' && <SupplementReview claimId={claimId} />}
      {/* 🤖 El auto follow-up al adjuster por el estimado: en qué va, apagar/pausar/arrancar. */}
      {deptKey === 'supplements' && <div className="card"><AutoFollowUpCard claimId={claimId} /></div>}

      {/* ARRIBA DE TODO en Scheduling: a quién llamar del lado del seguro si hay que reagendar
          (Manuel 2026-09-23, caso Glenn Schreck). Con huella de quién lo puso. */}
      {deptKey === 'scheduling' && <InspectionContactBox claimId={claimId} />}

      {/* Case Overview DE PRIMERO (debajo de la foto/hero del claim), no al final.
          Scheduling lo pidió también (2026-08-21): quien agenda necesita saber de qué va el caso
          antes de llamar al homeowner, y lo tenía al fondo del tab o en otra pantalla. */}
      {(deptKey === 'claims' || deptKey === 'scheduling') && <CaseOverview claimId={claimId} />}

      {/* Los videos que el inspector subió desde el campo (ATR e informativos), justo debajo del Case
          Overview (pedido del dueño 2026-09-19), en Claims y en Scheduling. */}
      {(deptKey === 'claims' || deptKey === 'scheduling') && <ClaimVideosBox claimId={claimId} />}

      {/* LO QUE MANDÓ EL CONTRATISTA, arriba, donde se revisa el claim.
          Pedido de Manuel (2026-08-26): el contratista de Loukisha Walker subió su declaration page
          desde JobFlow y no se veía por ningún lado — estaba guardada, pero enterrada entre los
          20.959 archivos de la pestaña Files, que es la última que alguien abre revisando un claim
          nuevo.
          OJO — INTAKE SE MONTA EN TRES LUGARES Y ESTE NO ES EL PRINCIPAL. La pestaña Intake de
          ClaimDetail tiene su propio bloque y el drawer de las working views tiene el suyo; los
          dos montan este panel por su cuenta. Acá entra intake solo por la vista Claims, que pasa
          `owner_dept`. Ponerlo únicamente acá fue el error de la primera versión: parecía cubierto
          y no aparecía en ninguno de los dos lugares donde Manuel lo buscaba. */}
      {(deptKey === 'intake' || deptKey === 'claims') && <ContractorFilesPanel claimId={claimId} />}

      {/* Claims: módulo de EMAIL debajo del Case Overview y encima de Department Updates (pedido). */}
      {deptKey === 'claims' && emailModule}

      {/* Claims: Appointments (Scheduling) ARRIBA (pedido de Manuel) — que se vea de primero,
          no enterrado al final del tab. Las inspecciones/citas son lo primero que se consulta. */}
      {deptKey === 'claims' && <ClaimAppointmentsPanel claimId={claimId} atrSince={atrSince} inspectorNotes={inspectorNotes} />}

      {/* Department updates (nombrado, altura fija con scroll). En Claims van PLEGABLES (pedido del dueño
          2026-09-19: "permite que los updates se puedan recoger o desplegar"); abiertos por defecto. */}
      {deptKey === 'claims' ? (
        <details className="card fold" open>
          <summary style={{ cursor: 'pointer', fontWeight: 'var(--fw-semibold)' }}><MessageSquare size={14} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {deptTitle} Department Updates</summary>
          <div style={{ marginTop: 12 }}><ClaimChat claimId={claimId} channel={deptKey} title={`${deptTitle} Department Updates`} /></div>
        </details>
      ) : (
        <ClaimChat claimId={claimId} channel={deptKey} title={`${deptTitle} Department Updates`} />
      )}

      {/* Y las CITAS justo debajo de los updates (pedido de Scheduling, 2026-08-21). Estaban más
          abajo, después de las grabaciones, las tareas, el email y la subida de archivos: quien
          agenda lee el último update y lo siguiente que necesita es la cita, no cinco módulos. */}
      {deptKey === 'scheduling' && <ClaimAppointmentsPanel claimId={claimId} atrSince={atrSince} inspectorNotes={inspectorNotes} />}

      {/* Services Performed (tarp / mitigación / etc.): módulo compartido a nivel claim. Se
          QUITÓ del tab Claims a pedido de Manuel (ahí no aplica); sigue en Intake/Scheduling/
          Supplements/Payments/Overview. */}
      {deptKey !== 'claims' && <ClaimServices claimId={claimId} />}

      {/* Call recording: grabaciones (approval calls / disputas) con adjuster/homeowner/contractor —
          en TODOS los deptos. Guarda tipo, con quién, quién subió + fecha (activity log). */}
      <CallRecordings claimId={claimId} departmentKey={deptKey} />

      {/* Assign Task (debajo de Department Updates, en TODOS los deptos). */}
      <ClaimTasksBox claimId={claimId} />

      {/* Comunicación por email del departamento (recogido). En Claims ya va arriba. */}
      {deptKey !== 'claims' && emailModule}

      {/* Subir archivos: la IA los lee, clasifica y guarda en Files del claim. */}
      <ClaimFileUploadBox claimId={claimId} />

      {/* Info Pending resumido: Supplements y Claims lo ven y suben los archivos que llegan. */}
      {(deptKey === 'supplements' || deptKey === 'claims') && <InfoPendingBox claimId={claimId} sourceStage={deptKey === 'supplements' ? 'Supplementing' : 'First Estimate'} compact />}
      {/* Invoices del contratista (Manuel 2026-09-30): mismo módulo en Claims y Supplements. */}
      {(deptKey === 'supplements' || deptKey === 'claims') && <ContractorInvoicesBox claimId={claimId} sourceStage={deptKey === 'supplements' ? 'Supplementing' : 'First Estimate'} />}

      {/* Appointments (Scheduling) en TODOS los deptos (intake incluido). Los otros deptos ya lo
          tienen abajo con su render propio; acá cubrimos intake. */}
      {deptKey === 'intake' && <ClaimAppointmentsPanel claimId={claimId} atrSince={atrSince} inspectorNotes={inspectorNotes} />}

      {/* Herramientas propias del departamento (las citas ya van arriba, bajo los updates). */}
      {deptKey === 'scheduling' && <ClaimSchedulingExtras claimId={claimId} />}

      {/* Claims: ATR. (Appointments se movió ARRIBA, después del Case Overview.) */}
      {deptKey === 'claims' && <ClaimAtrBox claimId={claimId} />}

      {/* Supplements: reportes de inspección + files SOLO estimados (sin Negotiation Coach). */}
      {deptKey === 'supplements' && <ClaimAppointmentsPanel claimId={claimId} atrSince={atrSince} inspectorNotes={inspectorNotes} />}
      {deptKey === 'supplements' && <ClaimFiles claimId={claimId} estimatesOnly />}
    </div>
  );
}
